/**
 * useWarehouseAccounts — shared data + actions for the account-centric warehouse
 * settings flow. Wraps the `settings.warehouses` oRPC procedures and exposes the
 * per-type test / save handlers, a single remove handler, prefill derivers, and
 * the synced warehouse list.
 *
 * The hook holds no per-type form state — each form component owns its own state
 * while open, so the hook only tracks transient test results keyed by connection
 * key.
 */
import { type PostgresPrefillConfig, postgresPrefillFromRsConfig } from '@server/postgres';
import { redshiftPrefillFromRsConfig } from '@server/redshift';
import type {
  BigQueryConnectionConfig,
  BigQueryRsConfig,
  ClickHouseConnectionConfig,
  DatabricksConnectionConfig,
  DatabricksRsConfig,
  PostgresConnectionConfig,
  RedshiftConnectionConfig,
  RedshiftRsConfig,
  SnowflakeConnectionConfig,
  SnowflakeConnectionInput,
  SnowflakeRsConfig,
  WarehouseAccountRow,
} from '@server/types';
import type { WarehouseProblem } from '@server/warehouse-failure';
import { connectionDisplayLabel, disambiguateConnectionNames } from '@server/warehouse-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';

import { isPermissionRefusal, refreshPermissionOnRefusal } from './accountCreatePermission';
import { refusalMessage, refusalProblem } from './refusalCopy';
import type { TestResult, WarehouseAccountConfig } from './shared/formHandle';
import { testRequestFailed } from './shared/testRequestFailed';
import { defaultRedshiftServiceUsername, type SnowflakeNameSeed, snowflakeNameSeed } from './shared/utils';
import type { WarehouseTypeKey } from './warehouseTypes';

/**
 * The bucket identity a prefill deriver seeds from when no representative destination
 * resolves — i.e. a bucket the customer typed by hand (phase 1.5, AI-1370).
 */
export interface BucketDims {
  hostDim: string;
  dbDim: string;
}

/**
 * One stored connection (an "account"), straight from `listAccounts`, with a
 * derived display `name`. A connection covers its whole account bucket (connections carry no
 * schema). `linkedCount` can be 0 (orphaned connection — never hidden). Every other field is the
 * backend's `WarehouseAccountRow`, so a field added there arrives here without being copied.
 *
 * `rsAccountId` / `rsAccountName` stay optional: the server always sends them, but card and drawer
 * fixtures build accounts that were never bound.
 */
export type WarehouseAccount = Omit<WarehouseAccountRow, 'rsAccountId' | 'rsAccountName' | 'orientation'> & {
  /**
   * ACT2-1109: the background Warehouse orientation run. Optional for the same reason as the account
   * fields below: the server always sends it (null when no run was admitted), fixtures may omit it.
   */
  orientation?: WarehouseAccountRow['orientation'];
  /** Bound RudderStack account name; the card title unless Lookout-minted. */
  rsAccountName?: string | null;
  /**
   * The bound RudderStack account's id, or null when nothing is bound.
   *
   * Read for one thing (ACT2-877 item 7): a `platform` connection with NO account has no credential
   * anywhere — it was created secret-free — so unlike every other unlinked connection it cannot run a
   * query until an account is chosen for it, and the card has to say so rather than look ordinary.
   */
  rsAccountId?: string | null;
  /**
   * Display name: the bound account's name (unless Lookout-minted), else the first linked
   * destination's real `warehouses.name`, else the database dim — the same `connectionDisplayLabel`
   * the server uses.
   */
  name: string;
};

/** Phase 3c decision 4: the coordinates whose kept RudderStack account the admin can reconnect to. */
/** A refused Save the server described (ACT2-1077), for the coordinates it was raised on. */
export interface SaveProblem {
  type: string;
  hostDim: string;
  dbDim: string;
  problem: WarehouseProblem;
}

export interface RetainedPrompt {
  type: string;
  hostDim: string;
  dbDim: string;
  message: string;
}

/** Gives each `listAccounts` row its display `name` — the derivation is described in `useWarehouseAccounts`. */
export function nameWarehouseAccounts<
  T extends {
    id: string;
    accountKey: string;
    rsAccountName?: string | null;
    type: string;
    linkedDestinations: readonly { name: string }[];
  },
>(rows: readonly T[]): (T & { name: string })[] {
  const named = rows.map((row) => ({
    ...row,
    name: connectionDisplayLabel({
      rsAccountName: row.rsAccountName,
      destinationName: row.linkedDestinations[0]?.name,
      accountKey: row.accountKey,
      type: row.type,
    }),
  }));
  const names = disambiguateConnectionNames(named.map((row) => ({ name: row.name, id: row.id })));
  return named.map((row, index) => ({ ...row, name: names[index] ?? row.name }));
}

/**
 * Does the kept-account prompt belong to the configure step showing `typeKey` at `(hostDim, dbDim)`?
 * The prompt lives on the page-level hook, so without this a prompt raised for one add would render
 * on a different one.
 */
export function retainedPromptMatches<P extends { type: string; hostDim: string; dbDim: string }>(
  prompt: P | null | undefined,
  typeKey: WarehouseTypeKey,
  hostDim: string,
  dbDim: string,
): prompt is P {
  return (
    !!prompt &&
    prompt.type === WAREHOUSE_DISPATCH[typeKey].driverType &&
    prompt.hostDim === hostDim &&
    prompt.dbDim === dbDim
  );
}

interface UseWarehouseAccountsOptions {
  /** Called after a connection is successfully saved (used to close the add drawer). */
  onConnectionSaved?: () => void;
}

/** The type-specific config field added to the updateConnection payload, per warehouse type. */
type SaveConfigField =
  | { connectionConfig: SnowflakeConnectionInput }
  | { redshiftConnectionConfig: RedshiftConnectionConfig }
  | { postgresConnectionConfig: PostgresConnectionConfig }
  | { bigqueryConnectionConfig: BigQueryConnectionConfig }
  | { databricksConnectionConfig: DatabricksConnectionConfig }
  | { clickhouseConnectionConfig: ClickHouseConnectionConfig };

/**
 * Everything that varies per warehouse type: the driver string sent to the test
 * endpoint and how the connection config maps onto the updateConnection payload.
 */
const WAREHOUSE_DISPATCH: Record<
  WarehouseTypeKey,
  { driverType: string; toSavePayload: (config: WarehouseAccountConfig) => SaveConfigField }
> = {
  SNOWFLAKE: { driverType: 'SNOWFLAKE', toSavePayload: (c) => ({ connectionConfig: c as SnowflakeConnectionInput }) },
  RS: { driverType: 'RS', toSavePayload: (c) => ({ redshiftConnectionConfig: c as RedshiftConnectionConfig }) },
  POSTGRES: {
    driverType: 'POSTGRES',
    toSavePayload: (c) => ({ postgresConnectionConfig: c as PostgresConnectionConfig }),
  },
  BQ: { driverType: 'BIGQUERY', toSavePayload: (c) => ({ bigqueryConnectionConfig: c as BigQueryConnectionConfig }) },
  DATABRICKS: {
    driverType: 'DATABRICKS',
    toSavePayload: (c) => ({ databricksConnectionConfig: c as DatabricksConnectionConfig }),
  },
  CLICKHOUSE: {
    driverType: 'CLICKHOUSE',
    toSavePayload: (c) => ({ clickhouseConnectionConfig: c as ClickHouseConnectionConfig }),
  },
};

export function useWarehouseAccounts(options?: UseWarehouseAccountsOptions) {
  const queryClient = useQueryClient();
  const orpc = useOrpc();
  const { workspace } = useWorkspace();
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [testPhase, setTestPhase] = useState<'test' | 'save'>('test');
  const [retainedPrompt, setRetainedPrompt] = useState<RetainedPrompt | null>(null);
  const [saveProblem, setSaveProblem] = useState<SaveProblem | null>(null);

  // `list` stays the source for synced warehouses + per-destination connections —
  // used here only for the configure-form prefill (and the representative lookup).
  const listQueryOptions = orpc.settings.warehouses.list.queryOptions({ input: {} });
  const accountsQueryOptions = orpc.settings.warehouses.listAccounts.queryOptions();
  const bucketsQueryOptions = orpc.settings.warehouses.listBuckets.queryOptions();
  const unmappedQueryOptions = orpc.settings.warehouses.listUnmapped.queryOptions();
  // Not read here — only invalidated. The adopt surface renders the overview, and a connection
  // deleted from this hook leaves that surface still showing the account as held: Undo and Detach
  // then answer "That warehouse connection no longer exists" against a row the screen still offers.
  const rsAccountsOverviewQueryOptions = orpc.settings.warehouses.listRsAccountsOverview.queryOptions();

  const { data, isLoading: listLoading } = useQuery(listQueryOptions);
  const { data: accountsData, isLoading: accountsLoading } = useQuery(accountsQueryOptions);
  const { data: bucketsData } = useQuery(bucketsQueryOptions);
  const { data: unmappedData } = useQuery(unmappedQueryOptions);

  const warehouses = useMemo(() => data?.warehouses ?? [], [data?.warehouses]);
  const connections = useMemo(() => data?.connections ?? [], [data?.connections]);
  const buckets = useMemo(() => bucketsData?.buckets ?? [], [bucketsData?.buckets]);
  const unmapped = useMemo(() => unmappedData?.unmapped ?? [], [unmappedData?.unmapped]);
  const unmappable = useMemo(() => unmappedData?.unmappable ?? [], [unmappedData?.unmappable]);
  // `unmappedCount` comes from `listUnmapped` (the single source of the anti-join); `listAccounts`
  // no longer recomputes it.
  const unmappedCount = unmappedData?.unmappedCount ?? 0;

  const isLoading = listLoading || accountsLoading;

  const getConnection = useCallback(
    (connectionKey: string) => connections.find((c) => c.connectionKey === connectionKey),
    [connections],
  );

  /**
   * Accounts come straight from `listAccounts` — one entry per stored connection
   * row. We only derive a display `name`: the
   * bound account's name (unless Lookout-minted), then the first linked
   * destination's name, then the database dim — the *same* label the server
   * derives, `connectionDisplayLabel`.
   *
   * This used to fall back to the host dim (`accountKey.split('|')[1]`), which is
   * shared by every connection in a bucket: three Databricks connections in one
   * workspace all rendered as `adb-….azuredatabricks.net` and could not be told
   * apart (AI-1370). The host is still shown on the card, as the muted mono
   * identity line — subordinate to the title, not the identifier. Reusing the
   * server helper also means one connection has one name everywhere: the agent
   * path and the public MCP already render `qa.dbx_e2e`, so settings now matches.
   *
   * Neither source is unique, though. Two connections whose only difference is the
   * host render one label, and two destinations may share a name outright — so the
   * whole LIST settles collisions afterwards, on the final title, by appending a
   * short uuid suffix to the entries that would otherwise read identically.
   */
  const accounts = useMemo<WarehouseAccount[]>(
    () => nameWarehouseAccounts(accountsData?.accounts ?? []),
    [accountsData?.accounts],
  );

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: listQueryOptions.queryKey });
    void queryClient.invalidateQueries({ queryKey: accountsQueryOptions.queryKey });
    void queryClient.invalidateQueries({ queryKey: bucketsQueryOptions.queryKey });
    void queryClient.invalidateQueries({ queryKey: unmappedQueryOptions.queryKey });
    void queryClient.invalidateQueries({ queryKey: rsAccountsOverviewQueryOptions.queryKey });
    // An account added or removed flips its key's links and docs between Connected and Disconnected:
    // Linked repositories (30s stale time), the doc pages and Context Hub read that state.
    void queryClient.invalidateQueries({ queryKey: orpc.warehouseProjects.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.docs.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.contextHub.key() });
  }, [
    orpc,
    queryClient,
    listQueryOptions.queryKey,
    accountsQueryOptions.queryKey,
    bucketsQueryOptions.queryKey,
    unmappedQueryOptions.queryKey,
    rsAccountsOverviewQueryOptions.queryKey,
  ]);

  const syncMutation = useMutation(
    orpc.settings.warehouses.sync.mutationOptions({
      onSuccess: () => {
        invalidateAll();
        toast.success('Refreshed from RudderStack');
      },
      onError: (err) => {
        refreshPermissionOnRefusal(queryClient, orpc, err);
        toast.error(
          isPermissionRefusal(err)
            ? refusalMessage(err, 'Couldn’t refresh from RudderStack')
            : 'Couldn’t refresh from RudderStack',
        );
      },
    }),
  );

  const signIn = useRudderstackSignInAgain();
  const saveMutation = useMutation(
    orpc.settings.warehouses.updateConnection.mutationOptions({
      onSuccess: (_data, variables) => {
        invalidateAll();
        const isRemove =
          variables.connectionString === null &&
          !variables.connectionConfig &&
          !variables.redshiftConnectionConfig &&
          !variables.postgresConnectionConfig &&
          !variables.bigqueryConnectionConfig &&
          !variables.databricksConnectionConfig &&
          !variables.clickhouseConnectionConfig;
        if (isRemove) {
          toast.success('Account removed');
        } else {
          toast.success('Account saved');
          options?.onConnectionSaved?.();
        }
      },
      // Surface the server's refusal (e.g. the CONFLICT on a duplicate create, often another admin's
      // save a moment earlier) rather than a generic string.
      onError: (err, variables) => {
        refreshPermissionOnRefusal(queryClient, orpc, err);
        // ACT2-1124: in a sibling workspace the save writes RudderStack as the person first.
        if (signIn.toastIfReauth(err)) return;
        const message = refusalMessage(err, 'Failed to save account');
        // Decision 4: the deleted connection's account still exists in RudderStack — offer the reconnect.
        if ((err as { data?: { reason?: string } }).data?.reason === 'retained_account_available') {
          setRetainedPrompt({ type: variables.type, hostDim: variables.hostDim, dbDim: variables.dbDim, message });
          return;
        }
        // ACT2-1077: an Add the server described is shown in the drawer, beside the form it refused.
        // An edit or a delete keeps the toast, which reads the same problem through refusalMessage.
        const problem = refusalProblem(err);
        if (problem && variables.isCreate === true) {
          setSaveProblem({ type: variables.type, hostDim: variables.hostDim, dbDim: variables.dbDim, problem });
          return;
        }
        toast.error(message);
      },
    }),
  );

  // Decision 4: bind the account a deleted platform connection kept, for the prompted coordinates.
  const reconnectMutation = useMutation(
    orpc.settings.warehouses.reconnectRetainedAccount.mutationOptions({
      onSuccess: () => {
        invalidateAll();
        setRetainedPrompt(null);
        toast.success('Reconnected to the account RudderStack kept');
      },
      onError: (err) => {
        refreshPermissionOnRefusal(queryClient, orpc, err);
        toast.error(refusalMessage(err, 'Could not reconnect to the account'));
      },
    }),
  );
  const reconnectRetained = useCallback(
    (onDone?: () => void) => {
      if (!retainedPrompt) return;
      reconnectMutation.mutate(
        { type: retainedPrompt.type, hostDim: retainedPrompt.hostDim, dbDim: retainedPrompt.dbDim },
        { onSuccess: () => onDone?.() },
      );
    },
    [reconnectMutation, retainedPrompt],
  );

  const clearRetainedPrompt = useCallback(() => setRetainedPrompt(null), []);

  const writeTestResult = useCallback((result: Omit<TestResult, 'connectionKey'>, ck: string) => {
    setTestResults((prev) => ({ ...prev, [ck]: { ...result } }));
  }, []);

  // The test never reached a verdict: the request itself failed. Described like any other failure, with
  // the transport's own text as the detail (ACT2-1077).
  const writeTestError = useCallback((ck: string, err: unknown) => {
    const problem = testRequestFailed(err);
    setTestResults((prev) => ({ ...prev, [ck]: { success: false, error: problem.message, problem } }));
  }, []);

  const resetTestResult = useCallback((ck: string) => {
    setTestResults((prev) => {
      if (!(ck in prev)) return prev;
      const next = { ...prev };
      delete next[ck];
      return next;
    });
  }, []);

  const testMutation = useMutation(
    orpc.settings.warehouses.testConnection.mutationOptions({
      // Always key test results by the derived connectionKey so ConfigureStep and
      // the drawer header read the same entry (no more rudderDestinationId keying).
      onSuccess: (result, variables) => writeTestResult(result, variables.connectionKey ?? ''),
      onError: (err, variables) => {
        refreshPermissionOnRefusal(queryClient, orpc, err);
        writeTestError(variables.connectionKey ?? '', err);
      },
    }),
  );

  const isTesting = testMutation.isPending && testPhase === 'test';
  const isSaving = (testMutation.isPending && testPhase === 'save') || saveMutation.isPending;

  const buildTypedTestInput = useCallback(
    (connectionKey: string, config: unknown, warehouseType: string) => ({
      connectionKey,
      connectionString: JSON.stringify(config),
      warehouseType,
    }),
    [],
  );

  /**
   * Stable test-result key for a bucket + optional schema, derived exactly like
   * the connection_key the server stores (`type|host|db|schema`). The drawer,
   * ConfigureStep, and `saveConnection` all key by this so a passing test is read
   * back under the same entry it was written to.
   *
   * The prefix is the *canonical* type (`WarehouseTypeKey`: SNOWFLAKE/RS/POSTGRES/DATABRICKS/BQ),
   * which is what the server uses for `connection_key` — NOT the driver string
   * (`driverType`, e.g. `BIGQUERY`), which the server canonicalizes away (→ `BQ`).
   * Keying off `driverType` would drift from `WarehouseAccount.connectionKey`, so
   * lookups like `removeConnection`'s `resetTestResult` would silently miss for BQ.
   */
  const deriveTestKey = useCallback(
    (typeKey: WarehouseTypeKey, hostDim: string, dbDim: string): string => `${typeKey}|${hostDim}|${dbDim}|`,
    [],
  );

  /** Dispatch a Test to the type's driver. */
  const testConnection = useCallback(
    (typeKey: WarehouseTypeKey, connectionKey: string, config: WarehouseAccountConfig) => {
      setTestPhase('test');
      testMutation.mutate(buildTypedTestInput(connectionKey, config, WAREHOUSE_DISPATCH[typeKey].driverType));
    },
    [testMutation, buildTypedTestInput],
  );

  /**
   * Dispatch a Save: test first, then persist via the new bucket-identity input
   * (the server derives accountKey/connectionKey from hostDim/dbDim — the client
   * never sends a key). `isCreate` guards a fresh Add against overwriting an
   * existing connection (server replies CONFLICT, surfaced as a toast).
   */
  const saveConnection = useCallback(
    (
      typeKey: WarehouseTypeKey,
      hostDim: string,
      dbDim: string,
      config: WarehouseAccountConfig,
      isCreate: boolean,
    ): void => {
      const { driverType, toSavePayload } = WAREHOUSE_DISPATCH[typeKey];
      // Test under the bucket key — the same key the drawer header and ConfigureStep read — so a
      // save-time test failure lands on the key the UI actually renders.
      const testKey = deriveTestKey(typeKey, hostDim, dbDim);
      setSaveProblem(null);
      setTestPhase('save');
      void (async () => {
        try {
          const result = await testMutation.mutateAsync(buildTypedTestInput(testKey, config, driverType));
          // Failure is now visible via testResults[testKey]; just stop before persisting.
          if (!result.success) return;
          saveMutation.mutate({
            hostDim,
            dbDim,
            // Connections carry no schema; still sent so a pre-release pod (which requires the
            // field) accepts the save during a rolling deploy. Removed in the follow-up PR.
            schemaName: null,
            type: driverType,
            isCreate,
            connectionString: null,
            ...toSavePayload(config),
          });
        } catch {
          // testResults already captured the error in onError above
        }
      })();
    },
    [testMutation, saveMutation, buildTypedTestInput, deriveTestKey],
  );

  /**
   * Delete a connection. Splits the accountKey (`type|host|db`) back into the raw
   * dims the server needs, and sends a null connectionString with no config fields.
   */
  const removeConnection = useCallback(
    (account: WarehouseAccount) => {
      resetTestResult(account.connectionKey);
      const [, hostDim = '', dbDim = ''] = account.accountKey.split('|');
      saveMutation.mutate({
        hostDim,
        dbDim,
        // See `saveConnection`: kept for pre-release pods during a rolling deploy.
        schemaName: null,
        type: account.type,
        connectionString: null,
      });
    },
    [saveMutation, resetTestResult],
  );

  // --- Prefill derivers (from the sanitized RudderStack destination config) ---
  //
  // Every deriver takes the selected bucket as well as the representative destination id. Since
  // phase 1.5 (AI-1370) a bucket can be typed by hand, in which case no destination resolves and
  // `whId` is ''. The bucket's own dims are then the prefill: they are what the customer just
  // entered, and they are what the server re-derives the account key from, so seeding the form
  // with anything else would fail `assert<Type>ConfigMatchesBucketDims` on save.
  //
  // The fallback fires **only** when no destination resolves. A synced destination's config still
  // wins outright, empty fields included: a Redshift serverless bucket's host dim is its workgroup
  // name, an identity rather than a dialable endpoint, so overlaying the dim onto a blank synced
  // host would prefill (and lock) a host that cannot be connected to.
  const deriveSnowflakePrefill = useCallback(
    (whId: string, bucket: BucketDims): SnowflakeConnectionConfig | undefined => {
      const wh = warehouses.find((w) => w.rudderDestinationId === whId);
      const rsConfig = wh?.config as SnowflakeRsConfig | undefined;
      return {
        // The bucket dims are the fallback: a synced destination whose config omits `account` (or
        // `database`) still belongs to this bucket, and the form shows both fields read-only, so an
        // empty prefill left the customer stuck on a required field they cannot type into.
        account: rsConfig?.account?.trim() ? rsConfig.account : bucket.hostDim,
        database: rsConfig?.database?.trim() ? rsConfig.database : bucket.dbDim,
        warehouse: rsConfig?.warehouse ?? '',
        // Schema is left empty so a bucket connection defaults to bucket-wide; the user
        // enters it only to narrow the connection to a single namespace (invariant 7).
        schema: '',
        // ACT2-1116: the form derives the service user and role once it has the key fingerprint
        // (`deriveSnowflakeNameSeed`), so the prefill names neither.
        username: '',
        role: '',
        authType: 'managed_key',
      };
    },
    [warehouses],
  );

  /**
   * ACT2-1116: what the Snowflake form derives its service user and role names from, with the key
   * fingerprint it fetches itself. The bucket's dims, never the destination's name, so every
   * destination of one account, and every admin, gets the same names.
   */
  const deriveSnowflakeNameSeed = useCallback(
    (bucket: BucketDims): SnowflakeNameSeed | null => snowflakeNameSeed(workspace.id, bucket.hostDim, bucket.dbDim),
    [workspace.id],
  );

  const deriveBigqueryPrefill = useCallback(
    (whId: string, bucket: BucketDims): BigQueryConnectionConfig | undefined => {
      const wh = warehouses.find((w) => w.rudderDestinationId === whId);
      const rsConfig = wh?.config as BigQueryRsConfig | undefined;
      if (wh ? !rsConfig?.project && !rsConfig?.location : !bucket.hostDim) return undefined;
      return {
        projectId: (wh ? rsConfig?.project : bucket.hostDim) ?? '',
        location: rsConfig?.location ?? 'US',
        credentials: { client_email: '', private_key: '' },
      };
    },
    [warehouses],
  );

  const deriveDatabricksPrefill = useCallback(
    (whId: string, bucket: BucketDims): DatabricksConnectionConfig | undefined => {
      const wh = warehouses.find((w) => w.rudderDestinationId === whId);
      const rsConfig = wh?.config as DatabricksRsConfig | undefined;
      if (wh ? !rsConfig?.host : !bucket.hostDim) return undefined;
      const prefill: DatabricksConnectionConfig = {
        host: (wh ? rsConfig?.host : bucket.hostDim) ?? '',
        httpPath: rsConfig?.httpPath ?? rsConfig?.path ?? '',
        authType: 'pat',
      };
      const catalog = wh ? rsConfig?.catalog : bucket.dbDim || undefined;
      if (catalog) prefill.catalog = catalog;
      // Schema left empty → bucket-wide by default; user narrows explicitly (invariant 7).
      return prefill;
    },
    [warehouses],
  );

  const deriveRedshiftPrefill = useCallback(
    (whId: string, bucket: BucketDims): RedshiftConnectionConfig | undefined => {
      const wh = warehouses.find((w) => w.rudderDestinationId === whId);
      const rsConfig = wh?.config as RedshiftRsConfig | undefined;
      const mapped = redshiftPrefillFromRsConfig(rsConfig ?? {});
      // Schema left empty → bucket-wide by default; user narrows explicitly (invariant 7).
      return {
        ...mapped,
        ...(wh ? {} : { host: bucket.hostDim, database: bucket.dbDim }),
        schema: '',
        user: defaultRedshiftServiceUsername(wh?.name ?? ''),
      };
    },
    [warehouses],
  );

  const derivePostgresPrefill = useCallback(
    (whId: string, bucket: BucketDims): PostgresPrefillConfig | undefined => {
      const wh = warehouses.find((w) => w.rudderDestinationId === whId);
      const rsConfig = wh?.config as Record<string, string> | null | undefined;
      const mapped = postgresPrefillFromRsConfig(rsConfig ?? undefined);
      return {
        ...mapped,
        ...(wh ? {} : { host: bucket.hostDim, database: bucket.dbDim }),
        schema: '',
        user: defaultRedshiftServiceUsername(wh?.name ?? ''),
      };
    },
    [warehouses],
  );

  return {
    // data
    accounts,
    warehouses,
    buckets,
    unmapped,
    unmappable,
    unmappedCount,
    isLoading,
    getConnection,
    // status
    testResults,
    resetTestResult,
    isSaving,
    retainedPrompt,
    saveProblem,
    clearRetainedPrompt,
    reconnectRetained,
    isReconnecting: reconnectMutation.isPending,
    isTesting,
    // mutations
    syncMutation,
    // handlers (per-type dispatch by canonical type key)
    deriveTestKey,
    testConnection,
    saveConnection,
    removeConnection,
    // prefill
    deriveSnowflakePrefill,
    deriveSnowflakeNameSeed,
    deriveBigqueryPrefill,
    deriveDatabricksPrefill,
    deriveRedshiftPrefill,
    derivePostgresPrefill,
  };
}

export type UseWarehouseAccountsReturn = ReturnType<typeof useWarehouseAccounts>;

/** A distinct `(host, db)` bucket from `listBuckets` (inferred from the oRPC output). */
export type WarehouseBucket = UseWarehouseAccountsReturn['buckets'][number];
/**
 * A destination that cannot be mapped (null accountKey) from `listUnmapped`.
 *
 * Its sibling `WarehouseUnmappedRow` went with the "Unmapped destinations" section (ACT2-877): the
 * only reader of the remediable rows was that component. `unmapped` itself stays on the hook — the
 * Add-account flow reads `unmappedCount` off the same query.
 */
export type WarehouseUnmappableRow = UseWarehouseAccountsReturn['unmappable'][number];
